// Real API Service - Connects to Azure Functions backend
const API_BASE_URL = 'https://insiderpass-api.azurewebsites.net/api';

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

export function getToken(): string | null {
  return localStorage.getItem('authToken');
}

export function setToken(token: string): void {
  localStorage.setItem('authToken', token);
}

export function clearToken(): void {
  localStorage.removeItem('authToken');
  localStorage.removeItem('currentMember');
}

export function isAuthenticated(): boolean {
  const token = getToken();
  if (!token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

// ============================================================================
// API HELPER
// ============================================================================

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// TYPES
// ============================================================================

export interface Member {
  memberId: string;
  phoneNumber: string;
  email: string | null;
  firstName: string;
  lastName: string;
  currentPoints: number;
  lifetimePoints: number;
  currentTier: 'Bronze' | 'Silver' | 'Gold';
  enrolledDate: string;
  shopifyCustomerId: string | null;
  referralCode: string | null;
}

export interface Reward {
  rewardId: string;
  rewardName: string;
  rewardDescription: string | null;
  rewardCategory: 'Access' | 'Experience' | 'Discount' | 'Service';
  rewardType: 'insider_perk' | 'points_reward';
  pointsCost: number;
  minTierRequired: 'Bronze' | 'Silver' | 'Gold';
  requiresScheduling: boolean;
  advanceNoticeDays: number | null;
  availableQuantity: number | null;
  remainingQuantity: number | null;
  isEligible: boolean;
  canAfford: boolean;
}

export interface Tier {
  tierName: string;
  tierLevel: number;
  minimumPoints: number;
  minimumMonthlyVisits: number | null;
  minimumMonthlySpend: number | null;
}

export interface Redemption {
  redemptionId: string;
  rewardId: string;
  rewardName: string;
  rewardDescription: string | null;
  rewardCategory: string | null;
  pointsRedeemed: number;
  redemptionCode: string;
  redemptionStatus: 'Pending' | 'Confirmed' | 'Completed' | 'Cancelled';
  requestedDate: string | null;
  confirmedDate: string | null;
  fulfilledDate: string | null;
  createdDate: string;
  canCancel: boolean;
  instructions: string | null;
}

export interface CarrierLookup {
  carrierName: string | null;
  carrierType: string | null;
  mobileCountryCode: string | null;
  mobileNetworkCode: string | null;
}

export interface Transaction {
  id: string;
  type: 'earn' | 'redeem';
  points: number;
  description: string;
  channel: string;
  date: string;
  orderTotal: number | null;
}

export interface AppConfig {
  showReferralProgram: boolean;
  referralBonusPoints: number;
  signupBonusPoints: number;
}

// ============================================================================
// APP CONFIG API
// ============================================================================

let cachedConfig: AppConfig | null = null;

export async function getAppConfig(): Promise<AppConfig> {
  // Return cached config if available (refreshes on page reload)
  if (cachedConfig) return cachedConfig;

  try {
    const config = await apiRequest<AppConfig>('/config');
    cachedConfig = config;
    return config;
  } catch {
    // Return defaults if API fails
    return {
      showReferralProgram: true,
      referralBonusPoints: 50,
      signupBonusPoints: 25,
    };
  }
}

// ============================================================================
// AUTH API
// ============================================================================

export async function signup(
  phoneNumber: string,
  firstName: string,
  lastName: string,
  email?: string
): Promise<{ success: boolean; message: string; lookup?: CarrierLookup }> {
  const response = await apiRequest<{ success: boolean; message: string; lookup?: CarrierLookup }>('/members/signup', {
    method: 'POST',
    body: JSON.stringify({ phoneNumber, firstName, lastName, email }),
  });
  
  // Store lookup temporarily for verify step
  if (response.lookup) {
    sessionStorage.setItem('carrierLookup', JSON.stringify(response.lookup));
  }
  
  return response;
}

export async function login(
  phoneNumber: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest('/members/login', {
    method: 'POST',
    body: JSON.stringify({ phoneNumber }),
  });
}

export async function verify(
  phoneNumber: string,
  code: string,
  isLogin: boolean = false,
  firstName?: string,
  lastName?: string,
  email?: string,
  referralCode?: string
): Promise<{
  success: boolean;
  member: Member;
  token: string;
}> {
  // Get stored lookup data (from signup flow)
  const lookupStr = sessionStorage.getItem('carrierLookup');
  const lookup = lookupStr ? JSON.parse(lookupStr) : null;

  const response = await apiRequest<{
    success: boolean;
    member: Member;
    token: string;
  }>('/members/verify', {
    method: 'POST',
    body: JSON.stringify({
      phoneNumber,
      code,
      isLogin,
      firstName,
      lastName,
      email,
      lookup,
      referralCode
    }),
  });

  // Clear stored lookup
  sessionStorage.removeItem('carrierLookup');

  // Store token and member data
  if (response.success && response.token) {
    setToken(response.token);
    localStorage.setItem('currentMember', JSON.stringify(response.member));
  }

  return response;
}

export function logout(): void {
  clearToken();
}

// ============================================================================
// MEMBER API
// ============================================================================

export async function getCurrentMember(): Promise<Member> {
  const member = await apiRequest<Member>('/members/me');
  localStorage.setItem('currentMember', JSON.stringify(member));
  return member;
}

// ============================================================================
// REWARDS API
// ============================================================================

export async function getRewards(): Promise<{
  rewards: Reward[];
  member: { currentPoints: number; currentTier: string };
}> {
  return apiRequest('/rewards');
}

export async function getReward(rewardId: string): Promise<{
  reward: Reward;
  member: { currentPoints: number; currentTier: string };
}> {
  return apiRequest(`/rewards/${rewardId}`);
}

// ============================================================================
// TIERS API
// ============================================================================

export async function getTiers(): Promise<{ tiers: Tier[] }> {
  return apiRequest('/tiers');
}

// ============================================================================
// REDEMPTIONS API
// ============================================================================

export async function getRedemptions(): Promise<{ redemptions: Redemption[] }> {
  return apiRequest('/redemptions');
}

export async function redeemReward(
  rewardId: string,
  requestedDate?: string
): Promise<{
  success: boolean;
  redemption: {
    redemptionCode: string;
    rewardName: string;
    pointsRedeemed: number;
    confirmedDate: string | null;
    instructions: string;
  };
  memberPointsRemaining: number;
}> {
  return apiRequest('/redemptions', {
    method: 'POST',
    body: JSON.stringify({ rewardId, requestedDate }),
  });
}

// ============================================================================
// TRANSACTIONS API
// ============================================================================

export async function getTransactions(): Promise<{ transactions: Transaction[] }> {
  return apiRequest('/transactions');
}

// ============================================================================
// ACCOUNT HEALTH API
// ============================================================================

export interface MemberViolation {
  violationId: string;
  violationName: string;
  violationDescription: string;
  consequenceType: string;
  healthScoreImpact: number;
  notes: string | null;
  issuedDate: string;
  expiresDate: string;
  status: 'Active' | 'Expired' | 'Overturned';
  overturnedDate: string | null;
}

export interface AccountHealth {
  score: number;
  status: string;
  isSuspended: boolean;
  suspendedUntil: string | null;
  isBanned: boolean;
  violations: MemberViolation[];
}

export async function getAccountHealth(): Promise<AccountHealth> {
  return apiRequest('/members/me/health');
}

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

export const api = {
  signup,
  login,
  verify,
  logout,
  isAuthenticated,
  getCurrentMember,
  getRewards,
  getReward,
  getTiers,
  getRedemptions,
  redeemReward,
  getTransactions,
};

export default api;
